/**
 * PURPOSE: Matriz de salto alto y garrocha (`SVA`): filas = atletas, columnas = alturas.
 * RESPONSIBILITIES: Cada celda acepta cualquier combinación de O (válido), X (nulo) y - (pasa) de hasta
 *   N intentos por altura (marcación libre; N = "Intentos por altura", 3 por defecto, viaja en
 *   `RoundSave.attemptsPerHeight` y queda en la prueba). "Agregar altura" suma una columna (por defecto
 *   la última + `config.heights.stepCm`) que viaja en `RoundSave.heights`. Cada altura de la lista se
 *   puede corregir (clic en el encabezado; sus intentos pasan a la nueva altura) o quitar (si tiene
 *   intentos marcados, pide confirmación y los borra al guardar). Mejor altura, fallos, lugar y
 *   eliminación vienen del servidor.
 * COLLABORATORS: RoundEditor.jsx, gridCells.jsx, ConfirmDialog.jsx, utils/athleticsMarks.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/grids/VerticalGrid.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FiCheck, FiPlus, FiX } from "react-icons/fi";

import {
  VERTICAL_STATUS_CODES,
  formatHeightCm,
  parseHeightInput,
} from "../../../../utils/athleticsMarks.js";
import { heightAttemptsPatch, isServerInvalidCell } from "../../../../utils/athleticsRoundDraft.js";
import { FieldStatusSelect } from "./FieldGrid.jsx";
import { useConfirmDialog } from "../ConfirmDialog.jsx";
import { secondaryButtonClass } from "../ui.jsx";
import {
  CellInput,
  FrozenScroll,
  InstitutionCell,
  NumberCell,
  ParticipantCell,
  PlaceCell,
  RowActions,
  buildFrozenColumns,
  rowToneClass,
  tdClass,
  thCenterClass,
  thClass,
} from "./gridCells.jsx";

const frozen = buildFrozenColumns([
  ["order", 4],
  ["athlete", 12],
  ["institution", 5.5],
]);

const cellHelp = (attempts) => `O = válido · X = nulo · - = pasa, hasta ${attempts} por altura. Ej.: XO, XXX, -`;
const ATTEMPTS_PER_HEIGHT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const VerticalGrid = ({
  prueba,
  round,
  draft,
  setRow,
  setDraft,
  readOnly,
  localInvalidKeys,
  serverInvalid,
  changedIds,
  canRemove,
  rowActions,
  showWaPoints,
  nav,
}) => {
  const entries = useMemo(() => (Array.isArray(round?.entries) ? round.entries : []), [round]);
  const { confirm, dialog } = useConfirmDialog();
  const [editing, setEditing] = useState(null);

  const columns = useMemo(() => {
    const all = new Set((draft.heights || []).map(Number));
    Object.values(draft.rows || {}).forEach((row) =>
      Object.keys(row.heights || {}).forEach((cm) => all.add(Number(cm)))
    );
    return Array.from(all)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
  }, [draft]);

  const stepCm = Number(prueba?.config?.heights?.stepCm) || 5;
  const startCm = Number(prueba?.config?.heights?.startCm) || null;
  const lastCm = columns.length ? columns[columns.length - 1] : null;
  const suggestedCm = lastCm ? lastCm + stepCm : startCm;
  const [heightInput, setHeightInput] = useState(suggestedCm ? formatHeightCm(suggestedCm) : "");

  useEffect(() => {
    setHeightInput(suggestedCm ? formatHeightCm(suggestedCm) : "");
  }, [suggestedCm]);

  const addHeight = () => {
    const cm = parseHeightInput(heightInput);
    if (!cm) {
      toast.error("Escribe la altura en metros, por ejemplo 1.35.");
      return;
    }
    if (columns.includes(cm)) {
      toast.error(`La altura ${formatHeightCm(cm)} ya está en la lista.`);
      return;
    }
    setDraft((current) => ({
      ...current,
      heights: [...(current.heights || []), cm].sort((a, b) => a - b),
    }));
  };

  const removeHeight = async (cm) => {
    const marked = rowsWithAttempts(cm);
    if (marked > 0) {
      const ok = await confirm({
        title: `Quitar la altura ${formatHeightCm(cm)}`,
        message: `${marked === 1 ? "1 atleta tiene" : `${marked} atletas tienen`} intentos marcados en ${formatHeightCm(cm)} m. Se borrarán al guardar.`,
        confirmLabel: "Quitar altura",
        tone: "danger",
      });
      if (!ok) return;
    }
    if (editing?.cm === cm) setEditing(null);
    setDraft((current) => {
      const rows = {};
      Object.entries(current.rows || {}).forEach(([id, row]) => {
        const heights = { ...(row.heights || {}) };
        delete heights[cm];
        rows[id] = { ...row, heights };
      });
      return { ...current, heights: (current.heights || []).filter((value) => value !== cm), rows };
    });
  };

  const commitEdit = () => {
    if (!editing) return;
    const from = editing.cm;
    const to = parseHeightInput(editing.value);
    if (!to) {
      toast.error("Escribe la altura en metros, por ejemplo 1.35.");
      return;
    }
    if (to === from) {
      setEditing(null);
      return;
    }
    if (columns.includes(to)) {
      toast.error(`La altura ${formatHeightCm(to)} ya está en la lista.`);
      return;
    }
    setDraft((current) => {
      const rows = {};
      Object.entries(current.rows || {}).forEach(([id, row]) => {
        const heights = { ...(row.heights || {}) };
        if (from in heights) {
          heights[to] = heights[from];
          delete heights[from];
        }
        rows[id] = { ...row, heights };
      });
      const heights = (current.heights || []).filter((value) => value !== from);
      return { ...current, heights: [...heights, to].sort((a, b) => a - b), rows };
    });
    setEditing(null);
  };

  const attemptsPerHeight = Number(draft.attemptsPerHeight) || 3;

  const rowsWithAttempts = (cm) =>
    Object.values(draft.rows || {}).filter((row) => String(row.heights?.[cm] || "").trim() !== "").length;

  return (
    <div>
      {!readOnly ? (
        <div className="flex flex-wrap items-end gap-2 border-b border-slate-100 px-3 py-2">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Nueva altura (m)</span>
            <input
              type="text"
              inputMode="decimal"
              value={heightInput}
              onChange={(event) => setHeightInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addHeight();
                }
              }}
              placeholder="1.35"
              className="w-24 rounded-md border border-slate-300 px-2 py-1 text-center font-mono text-sm font-semibold outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </label>
          <button type="button" onClick={addHeight} className={secondaryButtonClass}>
            <FiPlus aria-hidden="true" /> Agregar altura
          </button>
          <span className="text-xs text-slate-400">Incremento sugerido: {stepCm} cm</span>
          <label className="ml-auto flex flex-col gap-1">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Intentos por altura</span>
            <select
              value={attemptsPerHeight}
              onChange={(event) => setDraft((current) => ({ ...current, attemptsPerHeight: Number(event.target.value) }))}
              className="w-20 rounded-md border border-slate-300 px-2 py-1 text-center text-sm font-semibold outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              title="Máximo de intentos que se pueden marcar en cada altura (se guarda para toda la prueba)"
            >
              {ATTEMPTS_PER_HEIGHT_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <p className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500">Intentos por altura: {attemptsPerHeight}</p>
      )}

      {!columns.length ? (
        <p className="px-4 py-3 text-sm italic text-slate-500">
          Aún no hay alturas en la competencia{readOnly ? "." : ": agrega la altura inicial."}
        </p>
      ) : null}

      <FrozenScroll>
        <table className="w-full min-w-[760px] table-auto">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th {...frozen("order", thCenterClass)}>Orden</th>
              <th {...frozen("athlete", thClass)}>Atleta</th>
              <th {...frozen("institution", thClass)} title="Universidad">Univ.</th>
              {columns.map((cm) => (
                <th key={cm} className={thCenterClass}>
                  {readOnly ? (
                    formatHeightCm(cm)
                  ) : editing?.cm === cm ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={editing.value}
                        onChange={(event) => setEditing({ cm, value: event.target.value })}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            commitEdit();
                          } else if (event.key === "Escape") {
                            event.preventDefault();
                            setEditing(null);
                          }
                        }}
                        autoFocus
                        aria-label={`Nueva altura en lugar de ${formatHeightCm(cm)}`}
                        className="w-16 rounded border border-indigo-300 px-1 py-0.5 text-center font-mono text-xs font-semibold normal-case outline-none focus:ring-2 focus:ring-indigo-100"
                      />
                      <button
                        type="button"
                        onClick={commitEdit}
                        className="rounded p-0.5 text-emerald-600 hover:bg-emerald-50"
                        aria-label={`Aplicar el cambio de la altura ${formatHeightCm(cm)}`}
                        title="Aplicar (Enter)"
                      >
                        <FiCheck aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        className="rounded p-0.5 text-slate-400 hover:bg-slate-100"
                        aria-label="Cancelar el cambio de altura"
                        title="Cancelar (Esc)"
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing({ cm, value: formatHeightCm(cm) })}
                        className="rounded px-1 font-mono hover:bg-indigo-50 hover:text-indigo-700"
                        aria-label={`Cambiar la altura ${formatHeightCm(cm)}`}
                        title="Cambiar esta altura (sus intentos pasan a la nueva)"
                      >
                        {formatHeightCm(cm)}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeHeight(cm)}
                        className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                        aria-label={`Quitar la altura ${formatHeightCm(cm)}`}
                        title="Quitar esta altura"
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </span>
                  )}
                </th>
              ))}
              <th className={thCenterClass}>Mejor</th>
              {showWaPoints ? <th className={thCenterClass}>Puntos WA</th> : null}
              <th className={thCenterClass} title="Fallos en la mejor altura">
                F. mejor
              </th>
              <th className={thCenterClass} title="Fallos totales">
                F. total
              </th>
              <th className={thCenterClass}>Lugar</th>
              <th className={thCenterClass}>Estado</th>
              {canRemove ? <th className={thCenterClass} aria-label="Acciones" /> : null}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, rowIndex) => {
              const row = draft.rows[entry.id];
              if (!row) return null;
              const name = entry.participant?.name || "atleta";
              return (
                <tr
                  key={entry.id}
                  className={`border-b border-slate-100 last:border-b-0 ${rowToneClass({
                    serverInvalid: serverInvalid?.rowId === entry.id,
                    changed: changedIds.has(entry.id),
                    muted: entry.eliminated === true,
                  })}`}
                >
                  <td {...frozen("order", `${tdClass} text-center`)}>
                    <NumberCell
                      value={row.order}
                      onChange={(value) => setRow(entry.id, { order: value })}
                      disabled={readOnly}
                      ariaLabel={`Orden de ${name}`}
                    />
                  </td>
                  <td {...frozen("athlete", tdClass)}>
                    <ParticipantCell participant={entry.participant} />
                    {entry.eliminated ? (
                      <span className="text-[10px] font-bold uppercase text-slate-400">Eliminado</span>
                    ) : null}
                  </td>
                  <td {...frozen("institution", tdClass)}>
                    <InstitutionCell institution={entry.participant?.institution} />
                  </td>
                  {columns.map((cm) => {
                    const value = row.heights?.[cm] ?? "";
                    return (
                      <td key={cm} className={`${tdClass} text-center`}>
                        <CellInput
                          value={value}
                          onChange={(raw) =>
                            setRow(entry.id, (current) => heightAttemptsPatch(current, cm, raw, attemptsPerHeight))
                          }
                          disabled={readOnly}
                          invalid={isServerInvalidCell(serverInvalid, {
                            rowId: entry.id,
                            kind: "height",
                            cellValue: value,
                            heightCm: cm,
                          })}
                          softInvalid={localInvalidKeys.has(`${entry.id}|height|${cm}`)}
                          inputRef={nav.register(rowIndex, `h${cm}`)}
                          onKeyDown={nav.onKeyDown(rowIndex, `h${cm}`)}
                          title={cellHelp(attemptsPerHeight)}
                          ariaLabel={`${formatHeightCm(cm)} m de ${name}`}
                          inputMode="text"
                          className={`${attemptsPerHeight > 3 ? "w-20" : "w-14"} uppercase`}
                        />
                      </td>
                    );
                  })}
                  <td className={`${tdClass} text-center text-sm font-black text-slate-900`}>{entry.best || "—"}</td>
                  {showWaPoints ? (
                    <td className={`${tdClass} text-center text-sm font-bold text-indigo-700`}>
                      {entry.waPoints ?? "—"}
                    </td>
                  ) : null}
                  <td className={`${tdClass} text-center text-sm text-slate-600`}>{entry.failuresAtBest ?? "—"}</td>
                  <td className={`${tdClass} text-center text-sm text-slate-600`}>{entry.totalFailures ?? "—"}</td>
                  <td className={`${tdClass} text-center`}>
                    <PlaceCell place={entry.placeInRound} />
                  </td>
                  <td className={`${tdClass} text-center`}>
                    <FieldStatusSelect
                      value={row.status}
                      serverStatus={entry.status}
                      onChange={(value) => setRow(entry.id, { status: value })}
                      disabled={readOnly}
                      ariaLabel={`Estado de ${name}`}
                      codes={VERTICAL_STATUS_CODES}
                    />
                  </td>
                  {canRemove ? (
                    <td className={`${tdClass} text-center`}>
                      <RowActions name={name} {...rowActions(entry)} />
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </FrozenScroll>
      {dialog}
    </div>
  );
};

export default VerticalGrid;
